import { useCallback, useEffect, useRef } from 'react';

// Custom hook for sticky header functionality in GenericTable
export const useStickyTableHeader = () => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const tableHeaderRef = useRef<HTMLTableSectionElement>(null);

  const handleScroll = useCallback(() => {
    if (!tableWrapperRef.current || !tableHeaderRef.current) return;

    const tableWrapper = tableWrapperRef.current;
    const tableHeader = tableHeaderRef.current;

    // Find the main scrolling container by traversing up
    let scrollingElement: HTMLElement | null = null;
    let parent = tableWrapper.parentElement;

    while (parent && parent !== document.body) {
      const styles = window.getComputedStyle(parent);
      // Look for the main page container that has overflow-y auto, scroll, or overflow auto
      if (
        styles.overflowY === 'auto' ||
        styles.overflowY === 'scroll' ||
        styles.overflow === 'auto' ||
        styles.overflow === 'scroll'
      ) {
        scrollingElement = parent;
        break;
      }
      parent = parent.parentElement;
    }

    if (!scrollingElement) {
      // Use window scroll if no container found
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const wrapperRect = tableWrapper.getBoundingClientRect();
      const tableWrapperTop = wrapperRect.top + scrollTop;

      // Check if user has scrolled past the table start position
      if (scrollTop > tableWrapperTop) {
        // Translate the header by the scroll difference to keep it at the top
        const translateY = scrollTop - tableWrapperTop;
        tableHeader.style.transform = `translateY(${translateY}px)`;
      } else {
        // Reset translation when not scrolled past header
        tableHeader.style.transform = 'translateY(0px)';
      }
      return;
    }

    const scrollTop = scrollingElement.scrollTop;
    // Get the table wrapper's position relative to the scrolling container
    const containerRect = scrollingElement.getBoundingClientRect();
    const wrapperRect = tableWrapper.getBoundingClientRect();
    // Calculate the wrapper's position relative to the container's scroll position
    const tableWrapperTop = wrapperRect.top - containerRect.top + scrollTop;

    // Check if user has scrolled past the table start position
    if (scrollTop > tableWrapperTop) {
      // Translate the header by the scroll difference to keep it at the top
      const translateY = scrollTop - tableWrapperTop;
      tableHeader.style.transform = `translateY(${translateY}px)`;
    } else {
      // Reset translation when not scrolled past header
      tableHeader.style.transform = 'translateY(0px)';
    }
  }, []);

  useEffect(() => {
    if (!tableWrapperRef.current) return;

    // Find the main scrolling container by traversing up
    let scrollingElement: HTMLElement | null = null;
    let parent = tableWrapperRef.current.parentElement;

    while (parent && parent !== document.body) {
      const styles = window.getComputedStyle(parent);
      // Look for the main page container that has overflow-y auto, scroll, or overflow auto
      if (
        styles.overflowY === 'auto' ||
        styles.overflowY === 'scroll' ||
        styles.overflow === 'auto' ||
        styles.overflow === 'scroll'
      ) {
        scrollingElement = parent;
        break;
      }
      parent = parent.parentElement;
    }

    if (!scrollingElement) {
      // Use window scroll if no container found
      handleScroll();
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }

    handleScroll();
    scrollingElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollingElement?.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return { tableWrapperRef, tableHeaderRef };
};
